import { InvoiceNotification, InvoiceSelectedOption } from '../types/invoiceNotification';
import { OptionAddon, Recipe, RecipeIngredient } from '../types/recipe';

export interface IngredientUsage {
  ingredientKey: string;
  ingredientName: string;
  usedAmount: number;
  unit: string;
}

export interface LineIngredientUsage extends IngredientUsage {
  source: string;
  formula: string;
}

export type TemperatureMode = 'hot' | 'iced';

const normalize = (value?: string) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/gi, 'd')
    .trim()
    .toLowerCase();

export const normalizeIngredientText = normalize;

type UnitDimension = 'mass' | 'volume' | 'other';
type NormalizedUnit = { dimension: UnitDimension; unit: string; factor: number };

const normalizeUnit = (unit?: string): NormalizedUnit => {
  const key = normalize(unit).replace(/[\s.]+/g, '') || 'phan';
  if (['kg', 'kilo', 'kilogram'].includes(key)) return { dimension: 'mass', unit: 'g', factor: 1000 };
  if (['g', 'gram', 'gam'].includes(key)) return { dimension: 'mass', unit: 'g', factor: 1 };
  if (['l', 'lit', 'liter', 'litre'].includes(key)) return { dimension: 'volume', unit: 'ml', factor: 1000 };
  if (['ml', 'mililit', 'milliliter', 'millilitre'].includes(key)) return { dimension: 'volume', unit: 'ml', factor: 1 };
  return { dimension: 'other', unit: key, factor: 1 };
};

export const getFallbackIngredientUsage = (invoice: InvoiceNotification): IngredientUsage | null => {
  const usedAmount = Number(invoice.quantity || 0);
  const ingredientName = (invoice.itemName || invoice.itemId || 'Món không tên').trim();
  if (usedAmount <= 0 || !ingredientName) return null;
  return { ingredientKey: normalize(ingredientName), ingredientName, usedAmount, unit: invoice.unit?.trim() || 'Phần' };
};

export const aggregateIngredientUsage = (items: IngredientUsage[]): IngredientUsage[] =>
  Array.from(items.reduce((map, item) => {
    const normalizedUnit = normalizeUnit(item.unit);
    const key = `${item.ingredientKey}__${normalizedUnit.unit}`;
    const normalizedItem = {
      ...item,
      ingredientKey: key,
      usedAmount: item.usedAmount * normalizedUnit.factor,
      unit: normalizedUnit.dimension === 'other' ? item.unit : normalizedUnit.unit,
    };
    const existing = map.get(key);
    map.set(key, existing ? { ...existing, usedAmount: existing.usedAmount + normalizedItem.usedAmount } : normalizedItem);
    return map;
  }, new Map<string, IngredientUsage>()).values());

export const isStockUnitCompatible = (usageUnit?: string, stockUnit?: string): boolean => {
  const usage = normalizeUnit(usageUnit);
  const stock = normalizeUnit(stockUnit);
  if (usage.dimension === 'other' || stock.dimension === 'other') {
    return normalize(usageUnit) === normalize(stockUnit);
  }
  return usage.dimension === stock.dimension && usage.unit === stock.unit;
};

/** Quy đổi lượng dùng trong công thức sang đúng đơn vị của dòng nhập kho trước khi trừ tồn. */
export const convertIngredientUsageToStockUnit = (
  usedAmount: number,
  usageUnit?: string,
  stockUnit?: string,
): number | null => {
  const amount = Number(usedAmount);
  if (!Number.isFinite(amount)) return null;

  const usage = normalizeUnit(usageUnit);
  const stock = normalizeUnit(stockUnit);
  if (usage.dimension === 'other' || stock.dimension === 'other') {
    return normalize(usageUnit) === normalize(stockUnit) ? amount : null;
  }
  if (usage.dimension !== stock.dimension || usage.unit !== stock.unit) return null;

  return (amount * usage.factor) / stock.factor;
};

export const parseIngredientAmount = (value?: string): { amount: number; unit: string } => {
  const raw = String(value || '').trim().replace(/,/g, '.');
  if (!raw || raw.toLowerCase() === 'x') return { amount: 0, unit: 'g' };
  const numbers = raw.match(/-?\d+(?:\.\d+)?/g)?.map(Number) || [];
  if (!numbers.length) return { amount: 0, unit: 'g' };
  const range = raw.match(/^\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
  const amount = range ? (Number(range[1]) + Number(range[2])) / 2 : numbers[0];
  const parsedUnit = normalizeUnit(raw.replace(/-?\d+(?:\.\d+)?/g, ''));
  const unit = parsedUnit.dimension === 'other' ? 'g' : parsedUnit.unit;
  const factor = parsedUnit.dimension === 'other' ? 1 : parsedUnit.factor;
  return { amount: amount * factor, unit };
};

const HOT_TOKENS = new Set(['hot', 'nong']);
const ICED_TOKENS = new Set(['ice', 'iced', 'cold', 'lanh', 'da']);
const TEMP_TOKENS = new Set(['hot', 'nong', 'ice', 'iced', 'cold', 'lanh', 'da']);

const tokenize = (value?: string): string[] =>
  normalize(value)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/** Option chỉ chọn nóng/đá (không phải option hạt có chữ HOT/ICE trong tên). */
export const isTemperatureOnlyOption = (option: InvoiceSelectedOption): boolean => {
  const tokens = tokenize(`${option.optionId || ''} ${option.optionName || ''}`);
  if (!tokens.some((token) => TEMP_TOKENS.has(token))) return false;
  if (isBeanLikeName(option.optionName) || isBeanLikeName(option.optionId)) return false;
  // pure temp if name is essentially temperature words
  const meaningful = tokens.filter((t) => !['stpv', 'opt', 'nhiet', 'do', 'temp', 'temperature'].includes(t));
  return meaningful.length > 0 && meaningful.every((t) => TEMP_TOKENS.has(t));
};

/** Option phục vụ / ly / mang đi — không ảnh hưởng định lượng NL. */
export const isNonConsumingOption = (option: InvoiceSelectedOption): boolean => {
  if (isTemperatureOnlyOption(option)) return true;
  const text = normalize(`${option.optionId || ''} ${option.optionName || ''}`);
  const keywords = [
    'mang di',
    'tai cho',
    'o lai',
    'ly cocktail',
    'loai ly',
    'phuc vu',
    'so thich phuc vu',
    'ly nhua',
    'ly giay',
    'cup',
  ];
  return keywords.some((k) => text.includes(k));
};

export const isBeanLikeName = (value?: string): boolean => {
  const text = normalize(value);
  if (!text) return false;
  const beanKeywords = [
    'hat', 'bean', 'coffee', 'cafe', 'ca phe', 'robusta', 'arabica', 'catimor', 'catuai',
    'typica', 'bourbon', 'moka', 'culi', 'ethi', 'ethiopia', 'bensa', 'kenya', 'guatemala',
    'gesha', 'geisha', 'colombia', 'blend', 'cold brew', 'sidamo', 'guji', 'sumatra',
    'honduras', 'brazil', 'arabusta',
  ];
  return beanKeywords.some((keyword) => text.includes(keyword));
};

export const isExtraCoffeeOption = (option: InvoiceSelectedOption): boolean => {
  const optionText = normalize(`${option.optionName || ''} ${option.optionId || ''}`);
  return (
    optionText.includes('extra coffee') ||
    optionText.includes('them shot') ||
    optionText.includes('extra shot') ||
    optionText.includes('shot extra') ||
    /stpv[-_]?them[-_]?shot/.test(optionText) ||
    optionText.includes('opt-ro-ex') ||
    optionText.includes('opt-ar-ex')
  );
};

const resolveOptionPrice = (option: InvoiceSelectedOption): number => {
  const directPrice = Number(option.price);
  if (!Number.isNaN(directPrice) && directPrice > 0) return directPrice;
  const amount = Number(option.amount);
  const quantity = Number(option.quantity || 0);
  if (!Number.isNaN(amount) && quantity > 0) return amount / quantity;
  if (!Number.isNaN(amount)) return amount;
  return 0;
};

const detectTemperatureFromText = (raw?: string): TemperatureMode | null => {
  const tokens = tokenize(raw);
  if (!tokens.length) return null;
  const hasHot = tokens.some((t) => HOT_TOKENS.has(t));
  const hasIced = tokens.some((t) => ICED_TOKENS.has(t));
  if (hasHot && !hasIced) return 'hot';
  if (hasIced && !hasHot) return 'iced';
  return null;
};

/**
 * Ưu tiên option nhiệt độ trên hóa đơn; nếu không có thì suy từ tên/mã món
 * (CUKCUK hay tách Americano nóng/đá thành 2 mã riêng).
 */
export const detectTemperatureMode = (
  options: InvoiceSelectedOption[] = [],
  item?: { itemId?: string; itemName?: string },
): TemperatureMode | null => {
  const hotCandidates = options.filter((option) => {
    if (isBeanLikeName(option.optionName) || isBeanLikeName(option.optionId)) return false;
    const tokens = tokenize(`${option.optionId || ''} ${option.optionName || ''}`);
    return tokens.some((t) => HOT_TOKENS.has(t));
  });
  const icedCandidates = options.filter((option) => {
    if (isBeanLikeName(option.optionName) || isBeanLikeName(option.optionId)) return false;
    const tokens = tokenize(`${option.optionId || ''} ${option.optionName || ''}`);
    return tokens.some((t) => ICED_TOKENS.has(t));
  });

  if (hotCandidates.length && !icedCandidates.length) return 'hot';
  if (icedCandidates.length && !hotCandidates.length) return 'iced';
  if (hotCandidates.length && icedCandidates.length) {
    const hotPriceScore = Math.max(...hotCandidates.map(resolveOptionPrice));
    const icedPriceScore = Math.max(...icedCandidates.map(resolveOptionPrice));
    return icedPriceScore > hotPriceScore ? 'iced' : 'hot';
  }

  return (
    detectTemperatureFromText(item?.itemName) ||
    detectTemperatureFromText(item?.itemId) ||
    null
  );
};

const hasAnyAmountForMode = (ingredients: RecipeIngredient[], mode: TemperatureMode) =>
  ingredients.some((ing) => {
    const value = parseIngredientAmount(mode === 'hot' ? ing.hotAmount : ing.icedAmount);
    return value.amount > 0;
  });

export const resolveEffectiveMode = (
  ingredients: RecipeIngredient[],
  mode: TemperatureMode | null,
): TemperatureMode | null => {
  if (!mode) return null;
  if (hasAnyAmountForMode(ingredients, mode)) return mode;
  const fallback: TemperatureMode = mode === 'hot' ? 'iced' : 'hot';
  return hasAnyAmountForMode(ingredients, fallback) ? fallback : mode;
};

export const resolveIngredientMeasurementByMode = (
  ingredient: RecipeIngredient,
  mode: TemperatureMode | null,
): { amount: number; unit: string } => {
  const hotValue = parseIngredientAmount(ingredient.hotAmount);
  const icedValue = parseIngredientAmount(ingredient.icedAmount);
  if (mode === 'hot') return hotValue.amount > 0 ? hotValue : icedValue;
  if (mode === 'iced') return icedValue.amount > 0 ? icedValue : hotValue;
  if (hotValue.amount > 0 && icedValue.amount <= 0) return hotValue;
  if (icedValue.amount > 0 && hotValue.amount <= 0) return icedValue;
  return hotValue.amount > 0 ? hotValue : icedValue;
};

/** Chuẩn hóa tên option để ghép STPV ↔ OPT / khác dấu. */
export const normalizeOptionMatchKey = (value?: string): string =>
  normalize(value)
    .replace(/^(stpv|opt)[-_\s]*/g, '')
    .replace(/\b(doi hat|them shot|thêm shot|extra coffee|option)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, '')
    .trim();

const optionCoreTokens = (value?: string): string => {
  // Keep distinctive tokens (extracoffee, robusta, arabica, bean names) for fuzzy match
  return normalize(value)
    .replace(/^(stpv|opt)[-_\s]*/g, '')
    .replace(/[^a-z0-9]+/g, '')
    .trim();
};

export const matchRecipeOptionAddon = (
  option: InvoiceSelectedOption,
  addons: OptionAddon[] = [],
): OptionAddon | undefined => {
  if (!addons.length) return undefined;
  const optionId = (option.optionId || '').trim();
  if (optionId) {
    const byId = addons.find((addon) => addon.optionId && addon.optionId.trim() === optionId);
    if (byId) return byId;
    // ignore case / separators
    const idKey = optionCoreTokens(optionId);
    const byIdLoose = addons.filter((addon) => optionCoreTokens(addon.optionId) === idKey);
    if (byIdLoose.length === 1) return byIdLoose[0];
  }

  const optionName = normalize(option.optionName);
  if (optionName) {
    const byName = addons.find((addon) => normalize(addon.optionName) === optionName);
    if (byName) return byName;
  }

  // "Thêm shot: EXTRA COFFEE - AR" vs "EXTRA COFFEE - AR"
  if (optionName) {
    const byContains = addons.filter((addon) => {
      const addonName = normalize(addon.optionName);
      if (!addonName) return false;
      return optionName.includes(addonName) || addonName.includes(optionName);
    });
    if (byContains.length === 1) return byContains[0];
  }

  const optionKey = optionCoreTokens(`${option.optionId || ''}${option.optionName || ''}`);
  if (!optionKey || optionKey.length < 4) return undefined;

  const compactHits = addons.filter((addon) => {
    const addonKey = optionCoreTokens(`${addon.optionId || ''}${addon.optionName || ''}`);
    if (!addonKey || addonKey.length < 4) return false;
    return addonKey === optionKey || addonKey.includes(optionKey) || optionKey.includes(addonKey);
  });
  if (compactHits.length === 1) return compactHits[0];
  return undefined;
};

export const findMissingOptionAddonWarnings = (
  invoice: InvoiceNotification,
  recipe?: Recipe | null,
): string[] => {
  if (!recipe) return [];
  return (invoice.selectedOptions || [])
    .filter((option) => {
      // selected when quantity > 0, or quantity omitted but option present
      if (option.quantity === undefined || option.quantity === null) {
        return !!(option.optionName || option.optionId);
      }
      return Number(option.quantity) > 0;
    })
    .filter((option) => !isNonConsumingOption(option))
    .filter((option) => !matchRecipeOptionAddon(option, recipe.optionAddons || []))
    .map((option) => `Option "${option.optionName || option.optionId || '-'}" chưa có nguyên liệu cộng thêm trong công thức.`);
};

const isBeanReplacementOption = (
  option: InvoiceSelectedOption,
  addonIngredient: RecipeIngredient,
  baseIngredients: RecipeIngredient[],
  baseMode: TemperatureMode | null,
  itemQuantity: number,
): boolean => {
  if (isTemperatureOnlyOption(option)) return false;
  if (isExtraCoffeeOption(option)) return false;

  const optionText = normalize(`${option.optionName || ''} ${option.optionId || ''}`);
  const addonText = normalize(addonIngredient.name);
  const hasPackageSizeToken = /\b\d+(?:[.,]\d+)?\s*(g|kg)\b/.test(optionText);
  const optionLooksBean = isBeanLikeName(option.optionName) || isBeanLikeName(option.optionId) || optionText.includes('doi hat') || optionText.includes('to robusta') || optionText.includes('to arabica');
  const addonLooksBean = isBeanLikeName(addonIngredient.name);
  const optionMentionsAddonName = Boolean(addonText) && optionText.includes(addonText);

  if (!optionLooksBean && !addonLooksBean && !optionMentionsAddonName) return false;

  const optionQty = Number(option.quantity || 0);
  if (itemQuantity > 0 && optionQty > 0 && optionQty !== itemQuantity) return false;

  const addonMode = resolveEffectiveMode(baseIngredients, baseMode);
  const addonAmountPerUnit = resolveIngredientMeasurementByMode(addonIngredient, addonMode).amount;
  if (addonAmountPerUnit <= 0) return false;

  const beanBaseCandidates = baseIngredients.filter((ing) => isBeanLikeName(ing.name));
  const baseCandidates = beanBaseCandidates.length ? beanBaseCandidates : baseIngredients;
  if (!baseCandidates.length) return false;

  const hasSameNameBaseIngredient = baseCandidates.some((baseIng) => normalize(baseIng.name) === addonText);
  if (hasPackageSizeToken && optionMentionsAddonName && hasSameNameBaseIngredient) return true;
  if (optionLooksBean && addonLooksBean && beanBaseCandidates.length > 0) return true;

  return baseCandidates.some((baseIng) => {
    if (normalize(baseIng.name) === addonText) return false;
    const baseAmount = resolveIngredientMeasurementByMode(baseIng, addonMode).amount;
    if (baseAmount <= 0) return false;
    return Math.abs(baseAmount - addonAmountPerUnit) < 0.0001;
  });
};

export const calculateInvoiceLineIngredients = (
  invoice: InvoiceNotification,
  recipe: Recipe,
): { modeLabel: string; lineItems: LineIngredientUsage[] } => {
  const itemQuantity = Number(invoice.quantity || 0);
  const selectedOptions = invoice.selectedOptions || [];
  const baseMode = detectTemperatureMode(selectedOptions, {
    itemId: invoice.itemId,
    itemName: invoice.itemName,
  });
  const effectiveBaseMode = resolveEffectiveMode(recipe.ingredients || [], baseMode);
  const modeLabel =
    effectiveBaseMode === 'hot' ? 'Hot' : effectiveBaseMode === 'iced' ? 'Iced' : 'Không xác định';

  const usageMap = new Map<string, LineIngredientUsage>();

  (recipe.ingredients || []).forEach((ingredient) => {
    const measurement = resolveIngredientMeasurementByMode(ingredient, effectiveBaseMode);
    if (measurement.amount <= 0 || itemQuantity <= 0) return;
    const usedAmount = measurement.amount * itemQuantity;
    const key = `${normalize(ingredient.name)}__${measurement.unit}`;
    const existing = usageMap.get(key);
    if (existing) {
      existing.usedAmount += usedAmount;
      existing.formula = `${existing.formula} + ${measurement.amount.toLocaleString('vi-VN', { maximumFractionDigits: 2 })} ${measurement.unit} × ${itemQuantity}`;
      return;
    }
    usageMap.set(key, {
      ingredientKey: key,
      ingredientName: ingredient.name,
      usedAmount,
      unit: measurement.unit,
      source: 'Công thức món',
      formula: `${measurement.amount.toLocaleString('vi-VN', { maximumFractionDigits: 2 })} ${measurement.unit} × ${itemQuantity}`,
    });
  });

  selectedOptions.forEach((option) => {
    if (isNonConsumingOption(option) && isTemperatureOnlyOption(option)) return;
    if (isTemperatureOnlyOption(option)) return;

    const matchedAddon = matchRecipeOptionAddon(option, recipe.optionAddons || []);
    if (!matchedAddon) return;

    const optionQuantity = Number(option.quantity || 0);
    const addonMultiplier = optionQuantity > 0 ? optionQuantity : itemQuantity > 0 ? itemQuantity : 1;
    const addonMode = resolveEffectiveMode(matchedAddon.ingredients || [], baseMode);
    (matchedAddon.ingredients || []).forEach((ingredient) => {
      const measurement = resolveIngredientMeasurementByMode(ingredient, addonMode);
      const amountPerOption = measurement.amount;
      if (amountPerOption <= 0) return;

      const usedAmount = amountPerOption * addonMultiplier;
      const key = `${normalize(ingredient.name)}__${measurement.unit}`;
      const existing = usageMap.get(key);
      const replaceBaseIngredientByBeanOption = isBeanReplacementOption(
        option,
        ingredient,
        recipe.ingredients || [],
        baseMode,
        itemQuantity,
      );
      const shouldReplaceBase = replaceBaseIngredientByBeanOption;

      if (shouldReplaceBase) {
        const candidateBaseKeys = Array.from(usageMap.values())
          .filter((entry) => entry.source.includes('Công thức món') && isBeanLikeName(entry.ingredientName))
          .filter((entry) => {
            if (replaceBaseIngredientByBeanOption) return true;
            if (entry.ingredientKey === key) return false;
            if (itemQuantity <= 0) return true;
            const basePerUnit = entry.usedAmount / itemQuantity;
            return Math.abs(basePerUnit - amountPerOption) < 0.0001 || amountPerOption >= basePerUnit;
          })
          .map((entry) => entry.ingredientKey);

        candidateBaseKeys.forEach((baseKey) => usageMap.delete(baseKey));
        usageMap.set(key, {
          ingredientKey: key,
          ingredientName: ingredient.name,
          usedAmount,
          unit: measurement.unit,
          source: `Thay thế bởi bonus option: ${option.optionName || option.optionId || '-'}`,
          formula: `${amountPerOption.toLocaleString('vi-VN', { maximumFractionDigits: 2 })} ${measurement.unit} × ${addonMultiplier} (thay thế)`,
        });
        return;
      }

      if (existing) {
        existing.usedAmount += usedAmount;
        existing.source = `${existing.source} + Bonus option: ${option.optionName || option.optionId || '-'}`;
        const amt = amountPerOption.toLocaleString('vi-VN', { maximumFractionDigits: 2 });
        existing.formula = `${existing.formula} + ${amt} ${measurement.unit} × ${addonMultiplier}`;
      } else {
        usageMap.set(key, {
          ingredientKey: key,
          ingredientName: ingredient.name,
          usedAmount,
          unit: measurement.unit,
          source: `Bonus option: ${option.optionName || option.optionId || '-'}`,
          formula: `${amountPerOption.toLocaleString('vi-VN', { maximumFractionDigits: 2 })} ${measurement.unit} × ${addonMultiplier}`,
        });
      }
    });
  });

  return { modeLabel, lineItems: Array.from(usageMap.values()) };
};

export const calculateFallbackIdentityIngredients = (
  _invoice: InvoiceNotification,
): { modeLabel: string; lineItems: LineIngredientUsage[]; recipeStatus: string } => {
  return {
    modeLabel: 'Không xác định',
    lineItems: [],
    recipeStatus: 'Chưa có công thức',
  };
};

export const resolveInvoiceLineIngredients = (
  invoice: InvoiceNotification,
  recipe?: Recipe | null,
): { modeLabel: string; lineItems: LineIngredientUsage[]; recipeStatus: string } => {
  if (recipe) {
    return { ...calculateInvoiceLineIngredients(invoice, recipe), recipeStatus: 'Đã tính' };
  }
  return calculateFallbackIdentityIngredients(invoice);
};

/** Chuẩn hóa tên NL để khớp xuất ↔ nhập / catalog. */
export const normalizeIngredientForMatch = (value?: string): string => {
  const base = normalize(value);
  if (!base) return '';
  return base
    .replace(/[-_/()]/g, ' ')
    .replace(/\bethi\b/g, 'ethiopia')
    .replace(/\b\d+(?:[.,]\d+)?\s*(g|kg|ml|l)\b/g, ' ')
    .replace(/\b(hot|nong|ice|iced|cold|lanh|da)\b/g, ' ')
    .replace(/\b(hat|bean|goi|goi|cafe|ca phe|rang)\b/g, ' ')
    .replace(/\b(option|bonus|size|shot|x\d+)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/** Alias tên NL thường gặp (recipe → tên kho). */
export const INGREDIENT_NAME_ALIASES: Record<string, string[]> = {
  arabica: ['hat arabica', 'cafe arabica', 'ca phe arabica', 'arabica rang'],
  robusta: ['hat robusta', 'cafe robusta', 'ca phe robusta', 'robusta rang'],
  'sua tuoi': ['sua tuoi th', 'sua tuoi khong duong', 'fresh milk'],
  'sua dac': ['sua dac ong tho', 'sua dac ngoi sao'],
  'cot mo': ['mo', 'nuoc cot mo', 'puree mo'],
  'da vien': ['da', 'ice'],
};

export const ingredientNamesLooselyMatch = (left?: string, right?: string): boolean => {
  const a = normalizeIngredientForMatch(left);
  const b = normalizeIngredientForMatch(right);
  if (!a || !b) return false;
  if (a === b) return true;

  const expand = (name: string): string[] => {
    const keys = [name];
    Object.entries(INGREDIENT_NAME_ALIASES).forEach(([canon, aliases]) => {
      if (name === canon || aliases.includes(name) || name.includes(canon)) {
        keys.push(canon, ...aliases);
      }
    });
    return keys;
  };

  const leftKeys = new Set(expand(a));
  if (expand(b).some((key) => leftKeys.has(key))) return true;

  if (isBeanLikeName(left) && isBeanLikeName(right)) {
    const leftTokens = new Set(a.split(' ').filter(Boolean));
    const rightTokens = new Set(b.split(' ').filter(Boolean));
    const smaller = leftTokens.size <= rightTokens.size ? leftTokens : rightTokens;
    const larger = smaller === leftTokens ? rightTokens : leftTokens;
    return smaller.size >= 2 && Array.from(smaller).every((token) => larger.has(token));
  }

  return false;
};

export interface InventoryMatchCandidate {
  key: string;
  ingredientName: string;
  importedQuantity: number;
  unit?: string;
}

const comparableQuantity = (candidate: InventoryMatchCandidate): number => {
  const unit = normalizeUnit(candidate.unit);
  return Number(candidate.importedQuantity || 0) * unit.factor;
};

export const findBestInventoryMatchKey = (
  consumedIngredientName: string,
  candidates: InventoryMatchCandidate[],
): string | null => {
  const consumed = normalizeIngredientForMatch(consumedIngredientName);
  if (!consumed || !candidates.length) return null;

  const strict = candidates.filter(
    (candidate) => normalizeIngredientForMatch(candidate.ingredientName) === consumed,
  );
  if (strict.length) {
    return [...strict].sort((a, b) => comparableQuantity(b) - comparableQuantity(a))[0].key;
  }

  const loose = candidates.filter((candidate) =>
    ingredientNamesLooselyMatch(consumedIngredientName, candidate.ingredientName),
  );
  return loose.length === 1 ? loose[0].key : null;
};
